// What the Bodyweight trend chart draws over the plot after the series: the goal
// marker with its labels and the "PROJECTION" title.
// Free-form SVG, so it goes through recharts' <Customized>, which provides the
// axis scales. Positions come from bodyweightChartLayout.js.

import PropTypes from "prop-types";

import {
  firstScale,
  layoutProjection,
  layoutProjectionTitle,
  PROJECTION_LABEL_SIZE,
  toPixelShapes,
} from "./bodyweightChartLayout";
import {
  GOAL_COLOR,
  MUTED_COLOR,
  TREND_COLOR,
} from "./bodyweightChartTheme";

// The goal marker (ring + dot) and, when there is a projection, where the
// current pace lands, with their labels to the left of the dots.
const ProjectionOverlay = ({ layout, labels }) => {
  const { x, goalY, paceY, labelX, goalTextY, goalDateY, paceTextY } = layout;
  return (
    <g>
      {paceY === null ? null : (
        <circle
          cx={x}
          cy={paceY}
          r={5}
          fill="none"
          stroke={TREND_COLOR}
          strokeWidth={2}
        />
      )}
      <circle
        cx={x}
        cy={goalY}
        r={9}
        fill="none"
        stroke={GOAL_COLOR}
        strokeWidth={2}
      />
      <circle cx={x} cy={goalY} r={4.5} fill={GOAL_COLOR} />
      <text
        x={labelX}
        y={goalTextY}
        textAnchor="end"
        fill={GOAL_COLOR}
        fontSize={12}
        fontWeight={700}
      >
        {labels.goal}
      </text>
      <text
        x={labelX}
        y={goalDateY}
        textAnchor="end"
        fill={MUTED_COLOR}
        fontSize={11}
      >
        {labels.by}
      </text>
      {paceY === null ? null : (
        <text
          x={labelX}
          y={paceTextY}
          textAnchor="end"
          fill={TREND_COLOR}
          fontSize={12}
          fontWeight={600}
        >
          {labels.pace}
        </text>
      )}
    </g>
  );
};

ProjectionOverlay.propTypes = {
  layout: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
};

// Everything drawn over the plot after the series: the goal marker with its
// labels and the "PROJECTION" title. They are laid out together so none of the
// texts lands on another or on the series. Receives the chart's axis maps and
// plot offset from <Customized>.
export const ChartAnnotations = ({
  xAxisMap,
  yAxisMap,
  offset,
  rows,
  projection,
}) => {
  const shapes = toPixelShapes(
    rows,
    firstScale(xAxisMap),
    firstScale(yAxisMap)
  );
  const scales = { xAxisMap, yAxisMap, offset, shapes };
  const projectionLayout = projection
    ? layoutProjection({ ...scales, ...projection })
    : null;
  const avoid = projectionLayout ? [...projectionLayout.boxes] : [];
  const title =
    projectionLayout && projection.title
      ? layoutProjectionTitle({
          todayX: firstScale(xAxisMap)(projection.todayIdx),
          goalX: projectionLayout.x,
          plotTop: offset.top,
          plotBottom: offset.top + offset.height,
          text: projection.title,
          avoid,
          shapes,
        })
      : null;
  return (
    <g>
      {title ? (
        <text
          x={title.x}
          y={title.y}
          fill={MUTED_COLOR}
          fontSize={PROJECTION_LABEL_SIZE}
          fontWeight={700}
          letterSpacing="0.08em"
        >
          {projection.title}
        </text>
      ) : null}
      {projectionLayout ? (
        <ProjectionOverlay
          layout={projectionLayout}
          labels={projection.labels}
        />
      ) : null}
    </g>
  );
};

ChartAnnotations.propTypes = {
  xAxisMap: PropTypes.object,
  yAxisMap: PropTypes.object,
  offset: PropTypes.object,
  rows: PropTypes.array.isRequired,
  projection: PropTypes.object,
};
